import { type ButtonProps } from '@mui/material';
import { ComponentProps, memo, useMemo } from 'react';

import { Icon } from '@vestigas/ui';
import {
  type ButtonConfig,
  ButtonSplit,
} from '~/ui/molecules/ButtonSplit/ButtonSplit';

const ICON = {
  approve: <Icon metaphor="customSettledNotBilled" className="size-5" />,
  bill: <Icon metaphor="customSettled" className="size-5" />,
  csv: <Icon metaphor="backupTable" className="size-5" />,
  decline: <Icon metaphor="customNotSettled" className="size-5" />,
  excel: <Icon metaphor="backupTable" className="size-5" />,
  mapDirectDeliveryNote: <Icon metaphor="addLocation" className="size-5" />,
  pdf: <Icon metaphor="pictureAsPdf" className="size-5" />,
  permissionGrant: <Icon metaphor="lockOpenOutlined" className="size-5" />,
  requestSignature: <Icon metaphor="customUserSignature" className="size-5" />,
  shareDeliveryNote: <Icon metaphor="personAddFilled" className="size-5" />,
  signature: <Icon metaphor="customSignature" className="size-5" />,
} as const;

type MenuItemType = {
  isLoading?: boolean;
  name: string;
  onClick: () => void;
  selected?: boolean;
  styleOverrides?: {
    color?: string;
    fontWeight?: number;
  };
};

type DatagridToolbarCustomComponentProps = {
  readonly buttonProps?: ButtonProps;
  readonly className?: string;
  readonly onClickShowOptions?: () => void;
  readonly icon?: keyof typeof ICON;
  readonly menuItems: MenuItemType[];
  readonly title?: string;
};

export type ToolbarIconKey = ComponentProps<
  typeof DatagridToolbarCustomComponent
>['icon'];

export const DatagridToolbarCustomComponent = memo(
  ({
    buttonProps,
    className,
    onClickShowOptions,
    icon,
    menuItems,
    title, // Kept for backward compatibility, not currently used
  }: DatagridToolbarCustomComponentProps) => {
    void title;
    const startIcon = useMemo(() => {
      if (icon) {
        return ICON[icon];
      }

      return undefined;
    }, [icon]);

    const buttonSplitConfig: ButtonConfig = useMemo(() => {
      const firstItem = menuItems[0];

      if (!firstItem) {
        throw new Error('menuItems must have at least one item');
      }

      if (menuItems.length === 1) {
        // Single item: just primary button, no dropdown
        return {
          primary: {
            disabled: buttonProps?.disabled,
            isLoading: firstItem.isLoading,
            onClick: firstItem.onClick,
            styleOverrides: firstItem.styleOverrides,
            text: firstItem.name,
          },
        };
      }

      // Multiple items: first item as primary, all items in dropdown
      const config: ButtonConfig = {
        options: menuItems.map((item) => ({
          disabled: item.isLoading,
          isLoading: item.isLoading,
          onClick: item.onClick,
          styleOverrides: item.styleOverrides,
          text: item.name,
        })),
        primary: {
          disabled: buttonProps?.disabled,
          isLoading: firstItem.isLoading,
          onClick: firstItem.onClick,
          styleOverrides: firstItem.styleOverrides,
          text: firstItem.name,
        },
      };
      return config;
    }, [menuItems, buttonProps?.disabled]);

    return (
      <ButtonSplit
        className={className ?? ''}
        config={buttonSplitConfig}
        onClickShowOptions={onClickShowOptions}
        menuItemSx={{
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#f9fafb',
            },
          },
          '&:hover': {
            backgroundColor: '#f9fafb',
          },
          '&:last-child': {
            borderBottom: 'none',
          },
          borderBottom: '1px solid #e5e7eb',
          fontSize: '14px',
          minHeight: 'auto',
          px: 3,
          py: 1.5,
        }}
        menuPaperSx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          boxShadow:
            '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
          minWidth: 200,
          mt: 1,
          py: '8px',
        }}
        startIcon={startIcon}
        variant="outlined"
        buttonProps={{
          ...buttonProps,
          size: 'small',
        }}
      />
    );
  },
);

DatagridToolbarCustomComponent.displayName = 'DatagridToolbarCustomComponent';
